import { Link } from "react-router-dom";
import Table from "../../../../elements/table/Table";
import { lngDetect } from "../../../../../helpers/lngDetect";
import { formatEventDate } from "../../../../../util/Flag";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { dynamicOrderList } from "../../../../../services/orderService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Badge from "../../../../elements/Badge";
import dayjs from "dayjs";
import PopoverDropdown from "../../../../elements/PopoverDropdown";
import stripe from "../../../../../assets/images/stripe.svg";
import paypal from "../../../../../assets/images/paypalText.png";
import Image from "../../../../elements/Image";
import { t } from "i18next";

const statusMap = {
  processing: "processing",
  completed: "success",
  failed: "danger",
  pending: "pending",
  canceled: "danger",
  cancelled: "danger",
  confirmed: "confirmed",
  "partial-refund": "partialRefund",
};

export default function OrderDetails({ details = {} }) {
  const [searchText, setSearchText] = useState("");
  const { lang } = useSelector((state) => state?.langProvider);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const eventData = details?.eventData;
  const eventCreator = details?.userData;
  const paymentData = details?.payment;
  const promoterData = details?.promoterInfo;

  const filteredItems = useMemo(() => {
    if (!Array.isArray(details?.orderItemData)) return [];

    return details.orderItemData.filter((item) => {
      const fullName = `${item?.attendeeInfo?.firstName ?? ""} ${
        item?.attendeeInfo?.lastName ?? ""
      }`.toLowerCase();
      const barcode = item?.uid?.toLowerCase() ?? "";
      const ticket = item?.ticketInfo?.name?.en?.toLowerCase() ?? "";
      const query = searchText.toLowerCase();

      return (
        fullName.includes(query) ||
        barcode.includes(query) ||
        ticket.includes(query)
      );
    });
  }, [searchText, details?.orderItemData]);

  const filteredResult = useMemo(() => {
    return Object.values(
      filteredItems.reduce((acc, item) => {
        const name = item.ticketInfo?.name?.en || "Unknown";
        if (!acc[name]) acc[name] = { name, count: 0 };
        acc[name].count += 1;
        return acc;
      }, {})
    );
  }, [filteredItems]);

  const { data: orderFormData } = useQuery({
    queryKey: ["orderForm", filters, details?._id],
    queryFn: async () => {
      const res = await dynamicOrderList({
        ...filters,
        typeId: details?._id,
      });
      if (res?.docs?.status !== 200) {
        toast.warning(res?.message);
      }

      return res?.docs;
    },
    enabled: !!details?._id,
  });

  // Process form data for display
  const processedFormData = useMemo(() => {
    if (
      !orderFormData?.data ||
      !Array.isArray(orderFormData.data) ||
      orderFormData.data.length === 0
    ) {
      return [];
    }

    return orderFormData.data.map((form) => {
      const formType = form.formData?.type || "Custom Form";
      const formId = form.formData?.uid || form.formData?._id;

      return {
        id: form._id,
        formType:
          formType === "global-checkout" ? "Checkout Form" : "Custom Form",
        formId,
        answers:
          form.answers?.map((answer) => ({
            label: answer.fieldMeta?.label?.text?.en || "Unknown Field",
            value: answer.value,
            type: answer.fieldMeta?.type || "text",
            required: answer.fieldMeta?.required || false,
          })) || [],
      };
    });
  }, [orderFormData?.data]);

  const eventInfo = {
    title: lngDetect(eventData?.title) || "Demo Event",
    host:
      (eventCreator?.firstName ?? "") + " " + (eventCreator?.lastName ?? ""),
    details: [
      {
        icon: "fa-location-dot",
        text: [
          eventData?.address?.name,
          eventData?.address?.address1,
          eventData?.address?.state,
          eventData?.address?.country,
        ]
          .filter(Boolean)
          .join(", "),
        color: "text-primary",
      },
      {
        icon: "fa-calendar-days",
        text: formatEventDate(eventData?.startDate, eventData?.endDate),
        color: "text-primary",
      },
    ],
    orderNo: details?.orderId,
    status: details?.status?.info || "Pending",
    createdAt: details?.createdAt,
  };

  const tableHeadData = [
    {
      _id: "check-in",
      name: t("check-in"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "check-in on",
      name: t("check-in on"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "ticket",
      name: t("Ticket"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "price",
      name: t("Price"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "info",
      name: t("info"),
      align: "right",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
  ];

  const tableData = filteredItems?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: "name",
          type: "text",
          tdClasses: "px-6 py-4",
          data: item?.checkIn?.value ? (
            <Badge
              type={"success"}
              className={"!text-[12px] !py-1 rounded-md"}
              label="yes"
            />
          ) : (
            <Badge
              type={"danger"}
              className={"!text-[12px] !py-1 rounded-md"}
              label="no"
            />
          ),
        },
        {
          _id: "name",
          type: "text",
          tdClasses: "px-6 py-4",
          data: item?.checkIn?.value
            ? dayjs(item?.date).format("DD MMM, YYYY hh:mm A")
            : "-",
        },
        {
          _id: "ticket",
          type: "text",
          data: (
            <div>
              <div className="max-h-40 overflow-y-auto">
                <p className="text-deep-purple text-sm ">
                  {lngDetect(item?.ticketInfo?.name)}
                </p>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1">
                {t("eTicket")}{" "}
                {item?.fullPaid === "no" && (
                  <div className="flex items-center gap-1">
                    <Badge
                      type={"warning"}
                      className={"rounded-md"}
                      label={"booked"}
                    />
                  </div>
                )}
              </div>
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "price",
          type: "text",
          data: `${
            details?.eventData?.currency?.symbol || "$"
          } ${item?.finalPrice?.toFixed(2)}`,
          tdClasses: "px-6 py-4",
        },
        {
          _id: "price",
          align: "right",
          type: "text",
          data: (
            <PopoverDropdown
              buttonLabel={t("View Info")}
              position="left"
              buttonClass={"whitespace-nowrap"}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {(item?.attendeeData || [])?.map((item, idx) => (
                  <div className="relative" key={idx}>
                    <div className="text-slate-500">{t(item.question)}</div>
                    <div className="font-medium text-slate-800">
                      {item.answers}
                    </div>
                  </div>
                ))}
              </div>
            </PopoverDropdown>
          ),
          tdClasses: "px-6 py-4",
        },
      ],
    };
  });

  // Helper function to format field values based on type
  const formatFieldValue = (value, type) => {
    if (!value) return "Not provided";

    switch (type) {
      case "date":
        return new Date(value).toLocaleDateString();
      case "email":
        return (
          <a href={`mailto:${value}`} className="text-blue-600 hover:underline">
            {value}
          </a>
        );
      case "textarea":
        return (
          <div className="max-w-xs">
            <p className="whitespace-pre-wrap break-words">{value}</p>
          </div>
        );
      case "checkbox":
        // Handle checkbox arrays
        if (Array.isArray(value)) {
          return (
            <div className="flex flex-wrap gap-1">
              {value.map((item, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                >
                  {item?.label?.text?.en || item?.label || "Unknown"}
                </span>
              ))}
            </div>
          );
        }
        // Fallback for single checkbox value
        return value?.label?.text?.en || value?.label || String(value);
      case "radio":
      case "select":
        // Handle radio/select objects
        if (typeof value === "object" && value !== null) {
          return (
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
              {value?.label?.text?.en || value?.label || "Unknown"}
            </span>
          );
        }
        return String(value);
      default:
        // Handle any remaining object types
        if (typeof value === "object" && value !== null) {
          if (Array.isArray(value)) {
            return (
              <div className="space-y-1">
                {value.map((item, index) => (
                  <span key={index} className="block">
                    {typeof item === "object"
                      ? item?.label?.text?.en ||
                        item?.label ||
                        JSON.stringify(item)
                      : String(item)}
                  </span>
                ))}
              </div>
            );
          }
          return (
            value?.label?.text?.en || value?.label || JSON.stringify(value)
          );
        }
        return String(value);
    }
  };

  const tableHeadPromoterData = [
    {
      _id: "name",
      name: t("Name"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "commission",
      name: t("Commission"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "discount",
      name: t("Discount"),
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
  ];

  const tablePromoterData = promoterData?.info?.settingsPerTicket?.map(
    (item) => {
      return {
        _id: item?._id,
        rowData: [
          {
            _id: "name",
            type: "text",
            data: lngDetect(item?.ticket?.name),
            tdClasses: "px-6 py-4",
          },
          {
            _id: "commission",
            type: "text",
            data: "$" + item?.commission?.value?.toFixed(2),
            tdClasses: "px-6 py-4",
          },
          {
            _id: "discount",
            type: "text",
            data: item?.discount?.value?.toFixed(2) + "%",
            tdClasses: "px-6 py-4",
          },
        ],
      };
    }
  );

  const infoItems = [
    {
      label: `${t("Order No")}:`,
      value: (
        <span className="font-semibold text-slate-700">
          {eventInfo.orderNo}
        </span>
      ),
    },
    {
      label: `${t("Payment")}:`,
      value: (
        <Badge
          type={
            paymentData?.status.charAt(0).toUpperCase() +
            paymentData?.status.slice(1)
              ? "success"
              : "danger"
          }
          label={
            paymentData?.status.charAt(0).toUpperCase() +
              paymentData?.status.slice(1) || "Pending"
          }
          className="px-2 py-1 rounded-full text-xs font-medium capitalize"
        />
      ),
    },
    {
      label: `${t("Status")}:`,
      value: (
        <Badge
          type={statusMap?.[eventInfo.status]}
          label={eventInfo.status}
          className="px-2 py-1 rounded-full text-xs font-medium capitalize"
        />
      ),
    },
    {
      label: `${t("Refund or credit method")}:`,
      value: (
        <Image
          src={
            details?.payment?.gateway === "stripe"
              ? stripe
              : details?.payment?.gateway === "paypal" && paypal
          }
          classes="w-24 h-12 object-contain"
        />
      ),
    },
  ];

  return (
    <div className="w-full py-5 md:px-6 text-slate-800">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        <div className="bg-white/80 backdrop-blur rounded-3xl p-3 md:p-6 shadow-md lg:sticky lg:top-32 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 border border-transparent hover:border-primary/10 hover:no-underline">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">
            {t("Event Information")}
          </div>
          <div className="text-2xl font-extrabold mb-1">{eventInfo.title}</div>
          <div className="text-sm text-slate-500 mb-6">
            {t("Hosted by")} {eventInfo.host}
          </div>
          <div className="space-y-3">
            {eventInfo.details.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-primary/10 flex-shrink-0 border border-primary/10 flex items-center justify-center">
                  <i className={`fas ${item.icon} ${item.color}`} />
                </div>
                <div className="text-slate-600 text-sm">{item.text}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 mb-6 bg-gradient-to-br from-slate-50 to-white p-4 rounded-xl border border-slate-100 shadow-inner space-y-2 text-sm">
            {infoItems.map((item, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-slate-500 font-medium">{item.label}</span>
                {item.value}
              </div>
            ))}
            <div className="space-y-1 pt-6">
              <div className="flex items-center gap-1 justify-between font-medium">
                <span className="font-semibold text-slate-700">
                  {t("Sub-total price")}
                </span>
                {details?.eventData?.currency?.symbol || "$"}{" "}
                {parseFloat(details?.subtotal || 0)?.toFixed(2)}
              </div>
              <div className="flex items-center gap-1 justify-between font-medium">
                <span className="font-semibold text-slate-700">
                  {t("Transaction fee")}
                </span>
                {details?.eventData?.currency?.symbol || "$"}{" "}
                {parseFloat(details?.organizationTransactionFee || 0)?.toFixed(
                  2
                )}
              </div>
              <div className="flex items-center gap-1 justify-between font-medium">
                <span className="font-semibold text-slate-700">Sales Tax</span>
                {details?.eventData?.currency?.symbol || "$"}{" "}
                {parseFloat(details?.taxTotal || 0)?.toFixed(2)}
              </div>
              {details?.promoterDiscountTotal > 0 && (
                <div className="flex items-center gap-1 justify-between font-medium">
                  <span className="font-semibold text-slate-700">
                    {t("Promoter fee")}
                  </span>
                  <span className="text-red-400">
                    <i className="fa-light fa-minus"></i>{" "}
                    {details?.eventData?.currency?.symbol || "$"}{" "}
                    {parseFloat(details?.promoterDiscountTotal || 0)?.toFixed(
                      2
                    )}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1 border-t border-gray-200 pt-2 justify-between mt-1 font-medium">
                <span className="font-semibold text-slate-700">
                  {t("Total Price")}
                </span>
                {details?.eventData?.currency?.symbol || "$"}{" "}
                {parseFloat(details?.total || 0)?.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="pt-6 pb-4">
            <Link
              to={`/${lang}/user/events/event-manage/${eventData?.uid}`}
              className="text-white text-sm font-medium bg-primary-gradient px-6 rounded-full hover:opacity-90 transition-all duration-300 py-3"
            >
              {t("Manage Event")} →
            </Link>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="h-full overflow-y-auto pr-2 scrollbar space-y-5">
            <div className="flex flex-col md:flex-row justify-between items-center gap-1 md:gap-3 bg-slate-100 py-2 md:py-1 px-2 rounded-md">
              <h2 className="text-xl md:text-2xl font-bold whitespace-nowrap">
                {eventInfo.orderNo}
              </h2>
              <div className="text-sm text-slate-500 mt-1">
                {formatEventDate(eventInfo?.createdAt)}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur rounded-3xl p-4 md:p-6 border border-slate-200 shadow">
              <h3 className="text-lg font-bold mb-4">
                {t("Billing informations")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                {(details?.billingInfo || []).map((item, idx) => (
                  <div className="relative" key={idx}>
                    <div className="text-slate-500">{t(item.question)}</div>
                    <div className="font-medium text-slate-800">
                      {item.answers}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Data Section - Only show if data exists */}
            {processedFormData.length > 0 && (
              <div className="bg-white/80 backdrop-blur rounded-3xl p-6 border border-slate-200 shadow">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold">
                    {t("Additional Information")}
                  </h3>
                </div>

                <div className="space-y-6">
                  {processedFormData.map((form) => (
                    <div
                      key={form.id}
                      className="border border-slate-200 rounded-xl p-4"
                    >
                      {/* <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-slate-800">{form.formType}</h4>
                          {form.formId && <p className="text-xs text-slate-500 mt-1">ID: {form.formId}</p>}
                        </div>
                      </div> */}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {form.answers.map((answer, answerIndex) => (
                          <div key={answerIndex} className="space-y-1">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium text-slate-700">
                                {answer.label}
                              </label>
                              {answer.required && (
                                <span className="text-red-500 text-xs">*</span>
                              )}
                            </div>
                            <div className="text-sm text-slate-600">
                              {formatFieldValue(answer.value, answer.type)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {promoterData?.info && (
              <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow border border-gray-200 p-8 max-w-4xl mx-auto">
                <h3 className="text-lg font-semibold text-gray-800 mb-6 border-b pb-2">
                  {t("Promoter Information")}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t("Name")}
                    </label>
                    <p className="text-md font-semibold text-gray-800 capitalize">
                      {promoterData?.info?.displayName || "N/A"}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {t("Commission")}
                    </label>
                    <p className="text-md font-semibold text-gray-800">
                      {details?.eventData?.currency?.symbol || "$"}
                      {promoterData?.promoterFee?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4">
                    {t("Ticket Details")}
                  </h4>

                  <div className="relative">
                    <Table
                      tableHeadData={tableHeadPromoterData}
                      tableData={tablePromoterData}
                      pageCount={1}
                      currentPage={1}
                      isLoder={false}
                      isOrderingDisabled={true}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white/80 backdrop-blur rounded-3xl p-6 border border-slate-200 shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">{t("Attendees")}</h3>
              </div>

              <div className="relative">
                <Table
                  tableHeadData={tableHeadData}
                  tableData={tableData}
                  pageCount={1}
                  currentPage={1}
                  isLoder={false}
                  isOrderingDisabled={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
